import { useCore } from '@/composables/core/index.js'
import { FEATURE_FLAGS } from '@/lib/consts.js'
import { EVENT_ACTIONS, SCREEN_NAMES } from '@/lib/analytics/consts.js'
import { useDefaultRedirect } from '@/composables/default-redirect/index.js'
import { useAnalytics } from '@/composables/analytics/index.js'
import { useLoadingScreen } from '@/composables/loading-screen/index.js'
import { useDevLogger } from '@/composables/dev-logger/index.ts'
import { clearExperiments } from '@/composables/experiments/index.js'
import { createBooksyCardReader, destroyBooksyCardReader } from '@/composables/payments/booksy-card-reader/index.js'
import { useExtole } from '@/composables/extole/index.js'
import { useNativeIdentityService } from '@/composables/native-identity/index.js'
import { createIterableUserInstance, destroyIterableUserInstance } from '@/composables/marketing/iterable-embedded-messaging.js'
import { useEppo } from '@/composables/eppo/index.js'
import { useRedirection } from '@/composables/default-redirect/redirection.js'
import { LOGOUT_REASONS } from './consts.js'
export * from './consts.js'

async function login (identity, redirect = true, options = {}) {
  const { store } = useCore()
  const defaultRedirect = useDefaultRedirect()
  const logger = useDevLogger('login')
  const nativeIdentity = useNativeIdentityService()

  const {
    shouldAutoloadBusiness = true,
  } = options

  logger.debug(identity)
  store.dispatch('identity/login', identity)

  if (shouldAutoloadBusiness) {
    try {
      // BIL-553 - in the future that bit will be moved to billing-microservice
      const businessId = useRedirection().get()?.query?.active_business_id
      businessId && await store.dispatch('identity/selectBusiness', businessId)

      await store.dispatch('business/autoloadBusiness')
    } catch (e) {
      logger.error(e)

      if (e.response?.status >= 400) {
        logout(redirect, { reason: LOGOUT_REASONS.BUSINESS_DETAILS_ERROR })
        return Promise.reject(e)
      }
    }
  }

  if (nativeIdentity.isAvailable()) {
    nativeIdentity.login()
  }

  try {
    await createBooksyCardReader()
  } catch (e) {
    logger.error(e)
  }

  if (store.getters['app/isAndroidTablet']) {
    import('@/composables/google-play/index.js').then(({ useGooglePlay }) => {
      logger.debug('setting up google play subscription autorestore...')
      const { setupSubscriptionAutorestore } = useGooglePlay()
      setupSubscriptionAutorestore()
    })
  }

  try {
    if (useEppo().ff(FEATURE_FLAGS.Feature_IterableEmbeddedMessaging)) {
      await createIterableUserInstance()
    }
  } catch (e) {}

  useAnalytics().identify()

  // shortcut
  if (!redirect) return

  // redirect
  return defaultRedirect()
}

async function logout (redirect = true, options = {}) {
  const { store } = useCore()
  const analytics = useAnalytics()
  const defaultRedirect = useDefaultRedirect()
  const loadingScreen = useLoadingScreen()
  const logger = useDevLogger('logout')
  const nativeIdentity = useNativeIdentityService()

  const loginType = store.state.identity?.loginType

  useAnalytics().reset()

  logger.debug(redirect)

  loadingScreen.show()

  // log user out and invalidate currently used token
  await store.dispatch('api/logout')

  try {
    if (nativeIdentity?.isAvailable()) {
      await nativeIdentity.logout()
    }
  } catch (e) {
    logger.error(e)
  }

  try {
    await destroyBooksyCardReader()
  } catch (e) {
    logger.error(e)
  }

  clearExperiments()

  analytics.dispatch(analytics.EVENTS.LOGOUT)
  analytics.dispatch(analytics.EVENTS.LOGIN_LOGOUT_ACTION, {
    eventAction: EVENT_ACTIONS.USER_LOGGED_OUT,
    screenName: SCREEN_NAMES.LOGOUT_MODAL,
    eventReason: options?.reason,
    loginMethod: loginType,
  })

  await Promise.all([
    store.dispatch('identity/logout'),
    store.dispatch('business/reset'),
  ])

  useExtole().logout()

  // Intentional avoid ff here in case of instance was created and flag changed to false
  try {
    destroyIterableUserInstance()
  } catch (e) {}

  loadingScreen.hide()

  // shortcut
  if (!redirect) return

  // redirect
  return defaultRedirect()
}

export const useAuthentication = () => {
  return {
    login,
    logout,
  }
}
